/**
 * ⚠️⚠️ LA API NO ENTREGÓ TODO EL PERIODO PEDIDO.
 *
 * MEDIDO el 2026-09-14 con una cartera real de HyperLiquid: importando desde el
 * 12 de septiembre, `userFillsByTime` sólo sirvió fills desde el 14 a las 16:22
 * (la API conserva «the 10000 most recent fills») y el aviso post-sincronización
 * salió en verde. Dos días de operaciones —PnL realizado, ventas de spot— fuera
 * del libro y del informe fiscal sin que nada lo dijera.
 *
 * El backend detecta el hueco (`responseConfig.historyCap` +
 * `detectHistoryCoverageGap`) y este aviso lo pone delante con su consecuencia y
 * un remedio que NO promete completitud: la descarga «Trade History» de
 * HyperLiquid también puede venir limitada a los registros más recientes.
 *
 * La copia vive en `transactions.json` (`importCoverageGap.*`), igual que
 * `ImportSourceOverlapNotice`: el catálogo `imports.json` está en su techo de
 * tamaño.
 */

import type { ImportCoverageGap } from '@bat/shared/contracts/jobs/import-job.dto'
import { useTranslation } from 'react-i18next'

import { userConfigService } from '@/shared/services/user-config.service'

export interface ImportCoverageGapNoticeProps {
  gaps: readonly ImportCoverageGap[]
  testId?: string
}

export const ImportCoverageGapNotice = ({
  gaps,
  testId = 'import-coverage-gap-notice',
}: ImportCoverageGapNoticeProps) => {
  const { t } = useTranslation('transactions')
  if (gaps.length === 0) return null

  const hasHyperliquidGap = gaps.some((gap) => gap.platform === 'hyperliquid')

  return (
    <div
      data-testid={testId}
      role="alert"
      className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 text-xs text-amber-200 [html[data-theme=light]_&]:text-amber-800 space-y-1.5"
    >
      <p className="font-semibold text-sm" data-testid={`${testId}-title`}>
        ⚠ {t('importCoverageGap.title')}
      </p>
      <ul className="space-y-0.5">
        {gaps.map((gap) => {
          const params = {
            endpoint: gap.endpointKey,
            cap: userConfigService.formatNumberWithFixedDecimals(gap.maxRecords, 0),
            availableFrom: userConfigService.formatDate(gap.availableFrom, true),
          }
          return (
            <li
              key={gap.endpointKey}
              data-testid={`${testId}-period`}
              data-endpoint-key={gap.endpointKey}
              data-requested-from={gap.requestedFrom ?? ''}
              data-available-from={gap.availableFrom}
            >
              {gap.requestedFrom === null
                ? t('importCoverageGap.periodWithoutStart', params)
                : t('importCoverageGap.period', {
                    ...params,
                    requestedFrom: userConfigService.formatDate(gap.requestedFrom, true),
                  })}
            </li>
          )
        })}
      </ul>
      <p data-testid={`${testId}-consequence`}>{t('importCoverageGap.consequence')}</p>
      <p data-testid={`${testId}-remedy`}>
        {hasHyperliquidGap
          ? t('importCoverageGap.remedyHyperliquid')
          : t('importCoverageGap.remedyGeneric')}
      </p>
    </div>
  )
}
